import React from 'react';
import { Toaster, toast } from 'react-hot-toast';
import Icon from '../AppIcon';

/**
 * Toast Notification Component
 * Provides consistent toast notifications across the application
 */
export const ToastContainer = () => {
  return (
    <Toaster
      position="top-right"
      reverseOrder={false}
      gutter={8}
      toastOptions={{
        duration: 4000,
        style: {
          background: '#ffffff',
          color: '#1f2937',
          border: '1px solid #e5e7eb',
          borderRadius: '0.5rem',
          padding: '1rem',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          maxWidth: '400px'
        },
        success: {
          iconTheme: {
            primary: '#10b981',
            secondary: 'white'
          }
        },
        error: {
          iconTheme: {
            primary: '#ef4444',
            secondary: 'white'
          }
        }
      }}
    />
  );
};

/**
 * Toast Notification Service
 * Centralized service for showing toast notifications
 */
export const toastService = {
  /**
   * Show success toast
   * @param {string} message - Success message
   * @param {Object} options - Additional options
   */
  success(message, options = {}) {
    toast?.success(message, {
      ...options,
      icon: <Icon name="CheckCircle" size={20} className="text-green-500" strokeWidth={2} />
    });
  },

  /**
   * Show error toast
   * @param {string} message - Error message
   * @param {Object} options - Additional options
   */
  error(message, options = {}) {
    toast?.error(message, {
      ...options,
      icon: <Icon name="AlertCircle" size={20} className="text-red-500" strokeWidth={2} />
    });
  },

  /**
   * Show info toast
   * @param {string} message - Info message
   * @param {Object} options - Additional options
   */
  info(message, options = {}) {
    toast(message, {
      ...options,
      icon: <Icon name="Info" size={20} className="text-blue-500" strokeWidth={2} />
    });
  },

  /**
   * Show warning toast
   * @param {string} message - Warning message
   * @param {Object} options - Additional options
   */
  warning(message, options = {}) {
    toast(message, {
      ...options,
      icon: <Icon name="AlertTriangle" size={20} className="text-yellow-500" strokeWidth={2} />
    });
  },

  /**
   * Show booking confirmation toast
   * @param {Object} bookingData - Booking information
   */
  bookingConfirmation(bookingData) {
    const message = `Booking confirmed for ${bookingData?.trailerName || 'trailer'}`;
    this.success(message, {
      duration: 5000
    });
  },

  /**
   * Show check-in reminder toast
   * @param {Object} bookingData - Booking information
   */
  checkInReminder(bookingData) {
    const message = `Check-in reminder: ${bookingData?.trailerName || 'Your rental'} starts soon`;
    this.info(message, {
      duration: 6000
    });
  },

  /**
   * Show dispute update toast
   * @param {Object} disputeData - Dispute information
   */
  disputeUpdate(disputeData) {
    const message = `Dispute #${disputeData?.id || 'N/A'} status: ${disputeData?.status || 'Updated'}`;
    this.info(message, {
      duration: 5000
    });
  },

  /**
   * Show message delivery toast
   * @param {Object} messageData - Message information
   */
  messageDelivery(messageData) {
    const message = `New message from ${messageData?.senderName || 'User'}`;
    this.info(message, {
      duration: 4000
    });
  },

  /**
   * Show return inspection toast
   * @param {Object} inspectionData - Inspection information
   */
  returnInspection(inspectionData) {
    const message = `Return inspection ${inspectionData?.status || 'completed'}`;
    this.success(message, {
      duration: 5000
    });
  },

  /**
   * Show violation charge toast
   * @param {Object} chargeData - Charge information
   */
  violationCharge(chargeData) {
    const message = `Violation charge: $${chargeData?.amount || '0'} - ${chargeData?.reason || 'New charge'}`;
    this.warning(message, {
      duration: 6000
    });
  },

  /**
   * Show payment success toast
   * @param {Object} paymentData - Payment information
   */
  paymentSuccess(paymentData) {
    const message = `Payment successful: $${paymentData?.amount || '0'}`;
    this.success(message, {
      duration: 5000
    });
  },

  /**
   * Show payment failure toast
   * @param {Object} paymentData - Payment information
   */
  paymentFailure(paymentData) {
    const message = `Payment failed: ${paymentData?.error || 'Please try again'}`;
    this.error(message, {
      duration: 6000
    });
  },

  /**
   * Show review submitted toast
   * @param {Object} reviewData - Review information
   */
  reviewSubmitted(reviewData) {
    const message = `Review submitted successfully (${reviewData?.rating || '5'} stars)`;
    this.success(message, {
      duration: 5000
    });
  },

  /**
   * Show custom toast with icon
   * @param {string} message - Custom message
   * @param {string} iconName - Icon name from lucide-react
   * @param {Object} options - Additional options
   */
  custom(message, iconName = 'Bell', options = {}) {
    toast(message, {
      ...options,
      icon: <Icon name={iconName} size={20} className="text-blue-500" strokeWidth={2} />
    });
  },

  /**
   * Dismiss a specific toast
   * @param {string} toastId - Toast ID to dismiss
   */
  dismiss(toastId) {
    toast?.dismiss(toastId);
  },

  /**
   * Dismiss all toasts
   */
  dismissAll() {
    toast?.dismiss();
  },

  /**
   * Show payout confirmation toast
   * @param {Object} options - Payout information
   */
  payoutConfirmation: ({ amount, transactionId }) => {
    const message = `Payout of $${parseFloat(amount || 0)?.toFixed(2)} has been deposited to your bank account.`;
    const description = transactionId ? `Transaction ID: ${transactionId}` : undefined;
    toastService?.show(message, 'success', description);
  }
};

export default toastService;